import React from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, Bell, AlertTriangle, XCircle, Circle } from 'lucide-react';

interface MohreTimelineProps {
  currentDay: number;
  deadlineDays: number;
  isSubmitted: boolean;
}

interface Milestone {
  day: number;
  labelKey: string;
  defaultLabel: string;
  icon: React.ComponentType<{ className?: string }>;
}

export const MohreTimeline: React.FC<MohreTimelineProps> = ({ currentDay, deadlineDays, isSubmitted }) => {
  const { t } = useTranslation();

  const milestones: Milestone[] = [
    { day: 0, labelKey: 'payroll.complianceCenter.timeline.processed', defaultLabel: 'Payroll Processed', icon: CheckCircle2 },
    { day: 3, labelKey: 'payroll.complianceCenter.timeline.reminder', defaultLabel: 'Reminder', icon: Bell },
    { day: 10, labelKey: 'payroll.complianceCenter.timeline.escalation', defaultLabel: 'Escalation Alert', icon: AlertTriangle },
    { day: deadlineDays, labelKey: 'payroll.complianceCenter.timeline.late', defaultLabel: 'Officially Late', icon: XCircle },
  ];

  const statusFor = (m: Milestone): 'past' | 'active' | 'future' => {
    if (isSubmitted) return 'past';
    if (currentDay >= m.day) return currentDay === m.day ? 'active' : 'past';
    return 'future';
  };

  const iconColor = (m: Milestone) => {
    const s = statusFor(m);
    if (s === 'past') return 'text-green-500 bg-green-50 border-green-200';
    if (s === 'active') {
      if (m.day >= deadlineDays) return 'text-red-600 bg-red-50 border-red-300';
      if (m.day >= 10) return 'text-red-500 bg-red-50 border-red-200';
      if (m.day >= 3) return 'text-amber-500 bg-amber-50 border-amber-200';
      return 'text-green-500 bg-green-50 border-green-200';
    }
    return 'text-muted-foreground bg-muted border-border';
  };

  const lineColor = (fromDay: number) => {
    if (isSubmitted) return 'bg-green-400';
    if (currentDay > fromDay) return 'bg-green-400';
    return 'bg-border';
  };

  return (
    <div className="p-4 border border-border rounded-lg bg-card">
      <div className="flex items-center justify-between mb-4">
        <p className="font-semibold text-sm">{t('payroll.complianceCenter.timeline.title', 'MOHRE Escalation Timeline')}</p>
        <span className="text-xs text-muted-foreground">
          {isSubmitted
            ? t('payroll.complianceCenter.timeline.done', 'Submitted. Escalation stopped.')
            : t('payroll.complianceCenter.timeline.day', 'Day {{day}} of {{deadline}}', { day: currentDay, deadline: deadlineDays })}
        </span>
      </div>
      <div className="flex items-center w-full">
        {milestones.map((m, i) => {
          const Icon = isSubmitted && statusFor(m) === 'past' ? CheckCircle2 : m.icon;
          const pulsing = !isSubmitted && statusFor(m) === 'active';
          return (
            <React.Fragment key={m.day}>
              <div className="flex flex-col items-center shrink-0">
                <div className={`w-10 h-10 rounded-full border-2 flex items-center justify-center ${iconColor(m)} ${pulsing ? 'ring-4 ring-offset-2 ring-red-200 animate-pulse' : ''}`}>
                  <Icon className="w-5 h-5" />
                </div>
                <span className="mt-2 text-xs font-medium whitespace-nowrap">
                  {t(m.labelKey, m.defaultLabel)}
                </span>
                <span className="text-[10px] text-muted-foreground">
                  {t('payroll.complianceCenter.timeline.dayLabel', 'Day {{day}}', { day: m.day })}
                </span>
              </div>
              {i < milestones.length - 1 && (
                <div className={`flex-1 h-1 mx-2 ${lineColor(m.day)} rounded`} />
              )}
            </React.Fragment>
          );
        })}
      </div>
      {!isSubmitted && currentDay > 0 && (
        <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
          <Circle className="w-2 h-2 fill-current" />
          <span>{t('payroll.complianceCenter.timeline.current', 'Current position: day {{day}}', { day: currentDay })}</span>
        </div>
      )}
    </div>
  );
};
